import { useEffect, useRef, useState } from 'react';

/**
 * A search box that writes to the URL after a pause, without eating keys.
 *
 * Typing "printer" should be one request, not seven, so the box commits 300ms
 * after the last key. The box also follows the URL — Reset, another view, the
 * back button — and that used to be the bug: a key pressed after the commit
 * fired but before the URL caught up was overwritten by the older text coming
 * back, and as the box then matched the URL, nothing sent it again. Now the
 * box only follows the URL when it changes for some reason other than the box
 * itself having just sent it.
 */
export function useDebouncedSearch(
  committed: string,
  commit: (next: string) => void,
  delay = 300,
): [string, (next: string) => void, () => void] {
  const [draft, setDraft] = useState(committed);
  const lastSent = useRef(committed);
  const commitRef = useRef(commit);
  commitRef.current = commit;

  useEffect(() => {
    if (committed === lastSent.current) return;
    lastSent.current = committed;
    setDraft(committed);
  }, [committed]);

  useEffect(() => {
    if (draft === lastSent.current) return;
    const timer = setTimeout(() => {
      lastSent.current = draft;
      commitRef.current(draft);
    }, delay);
    return () => clearTimeout(timer);
  }, [draft, delay]);

  /** Sends what is typed now, without waiting — for Enter. */
  const flush = () => {
    if (draft === lastSent.current) return;
    lastSent.current = draft;
    commitRef.current(draft);
  };

  return [draft, setDraft, flush];
}

/** "#123" or "123": somebody who knows the ticket they want. */
export function ticketNumberIn(text: string): number | null {
  const match = /^#?\s*(\d{1,9})$/.exec(text.trim());
  if (!match) return null;
  const number = Number(match[1]);
  return number > 0 ? number : null;
}
